<script setup lang="ts">
import type { ButtonProps } from '/#/components/base/button/button'
import type {
  DownloadManagerItem,
  DownloadManagerItemProps,
} from '/#/components/base/downloads'

import get from 'lodash/get'

import { humanizeFileSize } from '/@/utils/formatter'
import { isEmpty } from '/@/utils/is'

const { t } = useI18n()

const props = defineProps<DownloadManagerItemProps>()

/**
 * @name fileItemStatusCheck
 * @type {Recordable<Partial<ButtonProps>>}
 * @description Variants to be introduced according to statuses.
 */
const fileItemStatusCheck: Recordable<Partial<ButtonProps>> = {
  COMPLETED: {
    icon: {
      name: 'certificate-checkmark',
      color: 'success',
    },
    text: t('common.completed'),
  },
  FAILED: {
    icon: {
      name: 'shield-protected',
      color: 'danger',
    },
    color: 'slate',
    text: t('common.failed'),
  },
  IN_PROGRESS: {
    icon: {
      name: 'loading-style-5',
      class: ['animate-spin', 'text-success'],
    },
    text: t('issues.in-progress'),
  },
}

/**
 * @name fileExtension
 * @type {ComputedRef<string|undefined>}
 * @description Extracts file extension from file.
 */
const fileExtension: ComputedRef<string | undefined> = computed(() =>
  props.file.name.split('.').pop()?.toLowerCase(),
)

/**
 * @method getStatusOfFile
 * @param {DownloadItem} file Holds file.
 * @description Making variant matching based on file status.
 */
function getStatusOfFile(file: DownloadManagerItem) {
  return get(fileItemStatusCheck, file.status)
}
</script>

<template>
  <div class="download-item-wrapper">
    <div v-if="!isEmpty(file)">
      <Flex flex-direction="row" justify-content="space-between" align-items="center">
        <Flex flex-direction="row" justify-start="justify-start">
          <FileItem :extension="fileExtension as string" class="text-slate-300" />
          <div class="download-info-wrapper">
            <Flex flex-direction="column">
              <div class="file-name">
                <TruncatedText :value="file.name" :text-length="30" />
              </div>
              <span class="file-size">
                {{ humanizeFileSize(file.downloadedSize) }} of
                {{ humanizeFileSize(file.totalSize) }}
              </span>
            </Flex>
          </div>
        </Flex>
        <Flex flex-direction="row" align-items="center" column-gap="1rem">
          <Badge
            v-if="file.status !== 'FAILED'"
            icon="download-arrow"
            :label="`${file.progress}%`"
            color="slate"
          />
          <Button
            v-bind="getStatusOfFile(file)"
            variant="link"
            color="black"
            no-padding
           
            class="pointer-events-none"
          />
        </Flex>
      </Flex>
    </div>
  </div>
</template>
